import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { pharmacyApi, BatchOption } from "@/api/pharmacy";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { Package, Loader2, Search, Plus, Download, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import { normalizeDateInputValue, validateDrugBatchExpiryInput } from "@/utils/dateUtils";
import { useAuth } from "@/contexts/AuthContext";

interface Drug {
  id: string;
  name: string;
  strength?: string;
  form?: string;
  unit?: string;
}

export default function PharmacyStockPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEnableEditGrace =
    user?.role === "PHARMACIST" ||
    user?.role === "HOSPITAL_ADMIN" ||
    user?.role === "SUPER_ADMIN";
  const [drugSearch, setDrugSearch] = useState("");
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [addDrugOpen, setAddDrugOpen] = useState(false);
  const [addDrugForm, setAddDrugForm] = useState({
    name: "",
    brandName: "",
    category: "OTHER",
    form: "TABLET",
    strength: "",
    unit: "",
    description: "",
  });

  const { data: drugs = [], isLoading: drugsLoading } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const filteredDrugs = drugSearch
    ? drugs.filter(
        (d) =>
          d.name?.toLowerCase().includes(drugSearch.toLowerCase()) ||
          d.strength?.toLowerCase().includes(drugSearch.toLowerCase())
      )
    : drugs;

  const { data: stock, isLoading: stockLoading } = useQuery({
    queryKey: ["pharmacy-stock", selectedDrugId],
    queryFn: async () => {
      if (!selectedDrugId) return null;
      const res = await pharmacyApi.getStock(selectedDrugId);
      return res.data?.data ?? null;
    },
    enabled: !!selectedDrugId,
  });

  const { data: editGrace } = useQuery({
    queryKey: ["pharmacy-stock-in-edit-grace"],
    queryFn: async () => {
      const res = await pharmacyApi.getStockInEditGrace();
      return res.data?.data ?? { active: false, until: null, source: null };
    },
  });

  const enableEditGraceMutation = useMutation({
    mutationFn: () => pharmacyApi.enableStockInEditGrace({ hours: 24 }),
    onSuccess: async (res) => {
      const until = res.data?.data?.until;
      toast.success(
        until
          ? `Batch edits enabled until ${format(new Date(until), "dd MMM yyyy HH:mm")}`
          : "Extended edit window enabled for 24 hours"
      );
      await queryClient.invalidateQueries({ queryKey: ["pharmacy-stock-in-edit-grace"] });
      await queryClient.invalidateQueries({ queryKey: ["pharmacy-batches"] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Could not enable edit window");
    },
  });

  const autoGraceBootstrapped = useRef(false);
  useEffect(() => {
    if (autoGraceBootstrapped.current || !canEnableEditGrace) return;
    if (editGrace === undefined || editGrace.active) return;
    autoGraceBootstrapped.current = true;
    enableEditGraceMutation.mutate();
  }, [canEnableEditGrace, editGrace, enableEditGraceMutation]);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ["pharmacy-batches", selectedDrugId, editGrace?.active, editGrace?.until],
    queryFn: async () => {
      if (!selectedDrugId) return [];
      const res = await pharmacyApi.getAllBatchesForDrug(selectedDrugId, {
        includeExpired: true,
        includeZero: true,
        includeEditable: true,
      });
      return (res.data?.data ?? []) as BatchOption[];
    },
    enabled: !!selectedDrugId,
  });

  const selectedDrug = selectedDrugId ? drugs.find((d) => d.id === selectedDrugId) : null;
  const isLoading = stockLoading || batchesLoading;
  const [downloading, setDownloading] = useState(false);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBatch, setEditBatch] = useState<BatchOption | null>(null);
  const [editForm, setEditForm] = useState({
    quantity: "",
    purchaseRate: "",
    gstRate: "",
    mrp: "",
    expiryDate: "",
    notes: "",
  });

  const openEdit = (batch: BatchOption) => {
    const e = batch.editableStockIn;
    if (!e) return;
    setEditBatch(batch);
    setEditForm({
      quantity: String(batch.available ?? e.batchOnHand ?? e.quantity),
      purchaseRate: e.purchaseRate != null ? String(e.purchaseRate) : "",
      gstRate: e.gstRate != null ? String(e.gstRate) : "",
      mrp: e.mrp != null ? String(e.mrp) : String(batch.mrp),
      expiryDate: normalizeDateInputValue(e.expiryDate ?? batch.expiryDate),
      notes: e.notes ?? "",
    });
    setEditOpen(true);
  };

  const editStockInMutation = useMutation({
    mutationFn: async () => {
      const e = editBatch?.editableStockIn;
      if (!e) throw new Error("Nothing to edit");
      const quantity = parseInt(editForm.quantity, 10);
      const purchaseRate = parseFloat(editForm.purchaseRate);
      const mrp = parseFloat(editForm.mrp);
      const gstRate = editForm.gstRate.trim() === "" ? null : parseFloat(editForm.gstRate);
      const expiryErr = validateDrugBatchExpiryInput(editForm.expiryDate);
      if (expiryErr) throw new Error(expiryErr);
      if (quantity == null || Number.isNaN(quantity) || quantity < 0) {
        throw new Error("Available quantity must be 0 or more");
      }
      if (quantity === 0) {
        throw new Error("Available must be at least 1; use Returns to clear stock");
      }
      if (!purchaseRate || purchaseRate <= 0) throw new Error("Purchase rate must be > 0");
      if (!mrp || mrp <= 0) throw new Error("MRP must be > 0");
      const payload = {
        quantity,
        purchaseRate,
        mrp,
        gstRate,
        expiryDate: normalizeDateInputValue(editForm.expiryDate),
        notes: editForm.notes.trim() || null,
      };
      if (e.batchDirectEdit || !e.ledgerId) {
        if (!editBatch?.batchId) throw new Error("Batch not found");
        return pharmacyApi.updateBatchStockCorrection(editBatch.batchId, payload);
      }
      return pharmacyApi.updatePurchaseStockIn(e.ledgerId, payload);
    },
    onSuccess: async () => {
      toast.success(
        editBatch?.editableStockIn?.batchDirectEdit
          ? "Batch updated (inventory adjusted)"
          : "Stock-in updated (ledger & inventory adjusted)"
      );
      setEditOpen(false);
      setEditBatch(null);
      await queryClient.invalidateQueries({ queryKey: ["pharmacy-stock", selectedDrugId] });
      await queryClient.invalidateQueries({ queryKey: ["pharmacy-batches", selectedDrugId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Update failed");
    },
  });

  const getExpiryTone = (expiryDate: string) => {
    const exp = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    exp.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { tone: "red" as const, label: "EXPIRED" };
    if (diffDays <= 30) return { tone: "yellow" as const, label: "NEAR" };
    return { tone: "green" as const, label: "OK" };
  };

  const downloadCsv = async () => {
    if (!selectedDrugId) return;
    try {
      setDownloading(true);
      const res = await pharmacyApi.exportStockCsv(selectedDrugId, { includeExpired: true, includeZero: true, location: "MAIN" });
      const blob = res.data as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = `${(selectedDrug?.name ?? "stock").replace(/[^a-z0-9]+/gi, "-")}`.replace(/-+/g, "-");
      a.href = url;
      a.download = `${safeName}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  const downloadFullInventoryCsv = async () => {
    try {
      setDownloadingAll(true);
      const res = await pharmacyApi.exportFullInventoryCsv({ asOf, includeExpired: true, includeZero: true, location: "MAIN" });
      const blob = res.data as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventory-MAIN-${asOf}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Inventory CSV downloaded");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to download inventory CSV");
    } finally {
      setDownloadingAll(false);
    }
  };

  const createDrugMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: addDrugForm.name.trim(),
        brandName: addDrugForm.brandName.trim() || undefined,
        category: addDrugForm.category,
        form: addDrugForm.form,
        strength: addDrugForm.strength.trim(),
        unit: addDrugForm.unit.trim(),
        description: addDrugForm.description.trim() || undefined,
      };
      if (!payload.name || !payload.category || !payload.form || !payload.strength || !payload.unit) {
        throw new Error("Required fields: name, category, form, strength, unit");
      }
      return api.post("/api/prescription/drugs", payload);
    },
    onSuccess: async () => {
      toast.success("Drug added to master");
      setAddDrugOpen(false);
      setAddDrugForm({
        name: "",
        brandName: "",
        category: "OTHER",
        form: "TABLET",
        strength: "",
        unit: "",
        description: "",
      });
      await queryClient.invalidateQueries({ queryKey: ["drugs-list"] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to add drug";
      toast.error(msg);
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Stock & Batches
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {canEnableEditGrace && !editGrace?.active ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={enableEditGraceMutation.isPending}
                  onClick={() => enableEditGraceMutation.mutate()}
                >
                  {enableEditGraceMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Clock className="h-4 w-4 mr-2" />
                  )}
                  Allow batch edits for 24 hours
                </Button>
              ) : null}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">As of</span>
                <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-9 w-[150px]" />
              </div>
              <Button type="button" variant="outline" onClick={downloadFullInventoryCsv} disabled={downloadingAll}>
                <Download className="h-4 w-4 mr-2" />
                {downloadingAll ? "Downloading..." : "Download full inventory CSV"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {editGrace?.active && editGrace.until ? (
            <p className="text-sm rounded-md border border-amber-200 bg-amber-50 text-amber-950 px-3 py-2">
              Extended correction window is active until{" "}
              <span className="font-medium">{format(new Date(editGrace.until), "dd MMM yyyy HH:mm")}</span>
              {editGrace.source === "env" ? " (server override)" : ""}. You can edit purchase stock-in on eligible
              batch rows below (not only within 24h of recording).
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Batch <strong>Edit</strong> is available for purchase stock-in within <strong>24 hours</strong> of
              recording, or for all recent purchase lines for <strong>24 hours</strong> after you click{" "}
              <strong>Allow batch edits for 24 hours</strong> above.
            </p>
          )}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search drug by name or strength..."
                value={drugSearch}
                onChange={(e) => setDrugSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!drugsLoading && filteredDrugs.length === 0 ? (
              <div className="flex items-center justify-between w-full gap-2">
                <p className="text-sm text-muted-foreground">No drugs found. Add drug master first.</p>
                <Button type="button" size="sm" variant="outline" onClick={() => setAddDrugOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add drug
                </Button>
              </div>
            ) : (
              filteredDrugs.slice(0, 30).map((d) => (
                <Button
                  key={d.id}
                  variant={selectedDrugId === d.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedDrugId(d.id)}
                >
                  {d.name} {d.strength && `(${d.strength})`}
                </Button>
              ))
            )}
            {filteredDrugs.length > 30 && (
              <span className="text-sm text-muted-foreground self-center">+{filteredDrugs.length - 30} more</span>
            )}
          </div>
          {selectedDrugId && (
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h3 className="font-medium">
                  {selectedDrug?.name} {selectedDrug?.strength} {selectedDrug?.form}
                </h3>
                <Button type="button" variant="outline" onClick={downloadCsv} disabled={downloading || isLoading}>
                  <Download className="h-4 w-4 mr-2" />
                  {downloading ? "Downloading..." : "Download CSV"}
                </Button>
              </div>
              {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : (
                <>
                  <p className="text-sm">
                    <strong>Available stock:</strong> {(stock as { quantity?: number })?.quantity ?? 0} {selectedDrug?.unit ?? "units"}
                  </p>
                  <div className="overflow-x-auto">
                  <Table className="min-w-[640px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Batch</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead>MRP</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>GST %</TableHead>
                        <TableHead>Available</TableHead>
                        <TableHead className="min-w-[7.5rem]">Edit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batches.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            No batches with stock
                          </TableCell>
                        </TableRow>
                      ) : (
                        batches.map((b) => (
                          <TableRow
                            key={b.batchId}
                            className={
                              getExpiryTone(b.expiryDate).tone === "red"
                                ? "bg-rose-50"
                                : getExpiryTone(b.expiryDate).tone === "yellow"
                                ? "bg-amber-50"
                                : "bg-emerald-50"
                            }
                          >
                            <TableCell className="font-mono">{b.batchNo}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span>{format(new Date(b.expiryDate), "dd MMM yyyy")}</span>
                                <span
                                  className={
                                    getExpiryTone(b.expiryDate).tone === "red"
                                      ? "text-xs font-medium text-rose-700"
                                      : getExpiryTone(b.expiryDate).tone === "yellow"
                                      ? "text-xs font-medium text-amber-700"
                                      : "text-xs font-medium text-emerald-700"
                                  }
                                >
                                  {getExpiryTone(b.expiryDate).label}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>{b.mrp}</TableCell>
                            <TableCell>
                              {b.purchaseRate != null && b.purchaseRate > 0
                                ? Number(b.purchaseRate).toFixed(2)
                                : "—"}
                            </TableCell>
                            <TableCell>{b.gstRate ?? "-"}%</TableCell>
                            <TableCell>{b.available}</TableCell>
                            <TableCell className="align-middle">
                              {b.editableStockIn ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  className="h-8 shrink-0 whitespace-nowrap px-3 bg-white shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
                                  onClick={() => openEdit(b)}
                                  title={`Edit stock-in until ${format(new Date(b.editableStockIn!.editableUntil), "dd MMM yyyy HH:mm")}`}
                                >
                                  <Pencil className="h-3.5 w-3.5 mr-1.5 shrink-0" aria-hidden />
                                  Edit
                                </Button>
                              ) : (
                                <span
                                  className="text-xs text-muted-foreground"
                                  title={
                                    editGrace?.active
                                      ? "No purchase stock-in line on this batch to edit"
                                      : "Edits within 24h of stock-in, or use “Allow batch edits for 24 hours” above"
                                  }
                                >
                                  —
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit recent stock-in</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {editGrace?.active
              ? "Extended correction window is active — edits update batch qty, rates, expiry, purchase ledger, and accounting."
              : "Corrections allowed for 24 hours after recording. Updates batch qty, rates, expiry, purchase ledger, and accounting."}
            {editBatch?.editableStockIn?.vendorInvoiceNumber ? (
              <>
                {" "}
                Invoice <span className="font-mono">{editBatch.editableStockIn.vendorInvoiceNumber}</span>
              </>
            ) : null}
          </p>
          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label>Available quantity (after save)</Label>
              <Input
                type="number"
                min={1}
                value={editForm.quantity}
                onChange={(e) => setEditForm((f) => ({ ...f, quantity: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Sets total batch stock to this number (not added on top). If this batch has other recent purchase
                lines, their ledger qty may be reduced so available matches.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Expiry</Label>
              <Input
                type="date"
                min="2000-01-01"
                max="2100-12-31"
                value={editForm.expiryDate}
                onChange={(e) => setEditForm((f) => ({ ...f, expiryDate: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>MRP (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={editForm.mrp}
                  onChange={(e) => setEditForm((f) => ({ ...f, mrp: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Purchase rate (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={editForm.purchaseRate}
                  onChange={(e) => setEditForm((f) => ({ ...f, purchaseRate: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>GST %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={editForm.gstRate}
                onChange={(e) => setEditForm((f) => ({ ...f, gstRate: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => editStockInMutation.mutate()} disabled={editStockInMutation.isPending}>
              {editStockInMutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add drug master dialog */}
      <Dialog open={addDrugOpen} onOpenChange={setAddDrugOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="add-drug-description">
          <DialogHeader>
            <DialogTitle>Add drug (master)</DialogTitle>
          </DialogHeader>
          <p id="add-drug-description" className="text-sm text-muted-foreground">
            Add a drug to the master list so you can manage batches and stock.
          </p>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              createDrugMutation.mutate();
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={addDrugForm.name} onChange={(e) => setAddDrugForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Paracetamol" />
              </div>
              <div className="space-y-1">
                <Label>Strength *</Label>
                <Input value={addDrugForm.strength} onChange={(e) => setAddDrugForm((f) => ({ ...f, strength: e.target.value }))} placeholder="e.g. 500mg" />
              </div>
              <div className="space-y-1">
                <Label>Form *</Label>
                <Select value={addDrugForm.form} onValueChange={(v) => setAddDrugForm((f) => ({ ...f, form: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TABLET","CAPSULE","SYRUP","INJECTION","CREAM","OINTMENT","DROPS","INHALER","PATCH","SUPPOSITORY","POWDER","GEL","LOTION","SPRAY","OTHER"].map((v) => (
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select value={addDrugForm.category} onValueChange={(v) => setAddDrugForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["ANTIBIOTIC","ANALGESIC","ANTI_INFLAMMATORY","CARDIOVASCULAR","RESPIRATORY","GASTROINTESTINAL","NEUROLOGICAL","PSYCHIATRIC","ENDOCRINE","DERMATOLOGICAL","OPHTHALMOLOGICAL","GYNECOLOGICAL","PEDIATRIC","EMERGENCY","VITAMIN_SUPPLEMENT","OTHER"].map((v) => (
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Unit *</Label>
                <Input value={addDrugForm.unit} onChange={(e) => setAddDrugForm((f) => ({ ...f, unit: e.target.value }))} placeholder="e.g. tab / ml / vial" />
              </div>
              <div className="space-y-1">
                <Label>Brand name</Label>
                <Input value={addDrugForm.brandName} onChange={(e) => setAddDrugForm((f) => ({ ...f, brandName: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Description</Label>
                <Input value={addDrugForm.description} onChange={(e) => setAddDrugForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional notes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDrugOpen(false)} disabled={createDrugMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={createDrugMutation.isPending}>
                {createDrugMutation.isPending ? "Saving..." : "Add drug"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
